import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Plus } from "lucide-react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useShop } from "../../contexts/ShopContext";
import { useSupportCenter } from "../../contexts/SupportCenterContext";
import { GET_SHOP_INTEGRATION_STATUS, GET_USER_ACCOUNT_TOKENS } from "../../graphql/queries";
import { DISCONNECT_GOOGLE, DISCONNECT_META, DISCONNECT_TIKTOK } from "../../graphql/mutations";
import GoogleConnectModal from "./GoogleConnectModal";
import MetaConnectModal from "./MetaConnectModal";
import TikTokConnectModal from "./TikTokConnectModal";
import { buildShopifyInstallUrl } from "../../utils/shopify/buildShopifyInstallUrl";
import styles from "./IntegrationsPanel.module.css";

// ── Brand icons (white SVG, 20×20) ───────────────────────────────────────────
const LogoImage = ({ src, alt, size = 20 }) => (
  <img
    src={src}
    alt={alt}
    width={size}
    height={size}
    className={styles.brandLogoImage}
  />
);

const MetaIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="white" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white" />
  </svg>
);

const TikTokIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.16 8.16 0 0 0 4.77 1.52V6.76a4.85 4.85 0 0 1-1-.07z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="white" stroke="none" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ShopifyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="11" width="3" height="8" rx="1.5" fill="white" />
    <rect x="10.5" y="6" width="3" height="13" rx="1.5" fill="white" />
    <rect x="17" y="3" width="3" height="16" rx="1.5" fill="white" />
  </svg>
);

const SearchConsoleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="11" height="14" rx="2.4" fill="white" opacity="0.98" />
    <circle cx="16.5" cy="15.5" r="3.2" stroke="white" strokeWidth="2" />
    <path d="M19 18L21 20" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <path d="M7.5 8H11.5" stroke="#4b7bec" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M7.5 11H11.5" stroke="#4b7bec" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MerchantCenterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M7 8.5C7 5.46 9.46 3 12.5 3C15.54 3 18 5.46 18 8.5V9H7V8.5Z" fill="white" />
    <path d="M5 9H20L18.6 19.2C18.46 20.2 17.61 21 16.6 21H8.4C7.39 21 6.54 20.2 6.4 19.2L5 9Z" fill="white" />
    <path d="M9.2 13.1H15.8" stroke="#4b7bec" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10.7 16H14.3" stroke="#4b7bec" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const WooCommerceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 7.4C4 5.52 5.52 4 7.4 4H16.6C18.48 4 20 5.52 20 7.4V13.6C20 15.48 18.48 17 16.6 17H11.7L8.1 20V17H7.4C5.52 17 4 15.48 4 13.6V7.4Z"
      fill="white"
    />
    <path
      d="M8.2 8.4L9.55 13L10.95 8.4H12.35L13.75 13L15.1 8.4H16.55L14.45 14.6H13.15L11.65 10L10.15 14.6H8.85L6.75 8.4H8.2Z"
      fill="#7f54b3"
    />
  </svg>
);

const KlaviyoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M6 5V19" stroke="white" strokeWidth="2.8" strokeLinecap="round" />
    <path d="M10.5 12L17.8 5.5" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.5 12L17.8 18.5" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
// ─────────────────────────────────────────────────────────────────────────────

const IntegrationCard = ({
  icon,
  badge,
  brandClassName,
  brandText,
  title,
  statusLabel,
  statusTone = "connected",
  detail,
  secondaryAction,
  secondaryTone = "neutral",
  primaryAction,
  primaryActionOnClick,
  primaryActionTone = "default",
  onClick,
  disabled = false,
}) => {
  const Tag = onClick && !disabled ? "button" : "div";

  return (
    <Tag
      type={onClick && !disabled ? "button" : undefined}
      className={`${styles.integrationCard} ${onClick && !disabled ? styles.integrationCardButton : ""} ${disabled ? styles.integrationCardDisabled : ""}`}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled ? "true" : undefined}
    >
      <div className={`${styles.integrationBrand} ${brandClassName || ""}`}>
        {icon || brandText}
      </div>
      <div className={styles.integrationTitle}>{title}</div>
      <div className={styles.integrationStatusRow}>
        <span
          className={`${styles.statusDot} ${
            statusTone === "connected"
              ? styles.statusDotConnected
              : statusTone === "warning"
                ? styles.statusDotWarning
                : styles.statusDotMuted
          }`}
          aria-hidden="true"
        />
        <span
          className={`${styles.integrationStatus} ${
            statusTone === "connected"
              ? styles.integrationStatusConnected
              : statusTone === "warning"
                ? styles.integrationStatusWarning
                : styles.integrationStatusMuted
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {detail ? <div className={styles.integrationDetail}>{detail}</div> : null}
      {secondaryAction || primaryAction ? (
        <div className={styles.integrationActions}>
          {secondaryAction ? (
            <span
              className={`${styles.integrationActionPill} ${
                secondaryTone === "accent"
                  ? styles.integrationActionPillAccent
                  : secondaryTone === "warning"
                    ? styles.integrationActionPillWarning
                    : styles.integrationActionPillNeutral
              }`}
            >
              {secondaryAction}
            </span>
          ) : null}
          {primaryAction ? (
            primaryActionOnClick ? (
              <button
                type="button"
                className={`${styles.integrationActionText} ${primaryActionTone === "danger" ? styles.integrationActionTextDanger : ""}`}
                onClick={(e) => { e.stopPropagation(); primaryActionOnClick(); }}
              >
                {primaryAction}
              </button>
            ) : (
              <span className={styles.integrationActionText}>{primaryAction}</span>
            )
          ) : null}
        </div>
      ) : null}
      {badge ? <div className={styles.integrationBadge}>{badge}</div> : null}
    </Tag>
  );
};

const IntegrationSection = ({ title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionBanner}>{title}</div>
    <div className={styles.sectionGrid}>{children}</div>
  </section>
);

const toneFor = (status) => {
  if (status === "connected") return "connected";
  if (status === "partial" || status === "mismatch") return "warning";
  return "muted";
};

const labelFor = (status, checking) => {
  if (checking) return "Checking connection...";
  if (status === "connected") return "Connected";
  if (status === "partial") return "Partially connected";
  if (status === "mismatch") return "Account mismatch";
  return "Not connected";
};

const IntegrationsPanel = () => {
  const { user, refreshUser } = useAuthUser();
  const { t } = useLocalization();
  const { shopData, selectedShopId, refreshShops } = useShop();
  const supportCenter = useSupportCenter();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const resolvedUserId = user?._id || user?.id || user?.userId || null;

  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [showTikTokModal, setShowTikTokModal] = useState(false);

  const { data, loading, refetch } = useQuery(GET_SHOP_INTEGRATION_STATUS, {
    skip: !authToken || !resolvedUserId,
    variables: { shopId: selectedShopId || null, userId: resolvedUserId },
    context: { headers },
    fetchPolicy: "cache-and-network",
  });
  const { data: tokenData, refetch: refetchTokens } = useQuery(
    GET_USER_ACCOUNT_TOKENS,
    {
      skip: !authToken,
      context: { headers },
      fetchPolicy: "network-only",
    },
  );

  const [runDisconnectGoogle, { loading: disconnectingGoogle }] = useMutation(DISCONNECT_GOOGLE, { context: { headers } });
  const [runDisconnectMeta, { loading: disconnectingMeta }] = useMutation(DISCONNECT_META, { context: { headers } });
  const [runDisconnectTikTok, { loading: disconnectingTikTok }] = useMutation(DISCONNECT_TIKTOK, { context: { headers } });
  const openShopifyConnect = useCallback(() => {
    window.open(buildShopifyInstallUrl(), "_blank", "noopener,noreferrer");
  }, []);

  const integrationStatus = data?.getShopIntegrationStatus;
  const accountTokens = tokenData?.getUserAccountTokens;
  // Only show loading state when there is no cached data at all.
  // cache-and-network sets loading=true on background refetches too,
  // which caused the "Checking..." flash on every page revisit.
  const checking = loading && !integrationStatus;
  const rawShopify = integrationStatus?.shopify;
  const rawGoogle = integrationStatus?.google;
  const rawMeta = integrationStatus?.meta;
  const tiktok = integrationStatus?.tiktok;

  const hasGoogleToken = Boolean(
    accountTokens?.googleToken ||
      accountTokens?.googleRefreshToken ||
      user?.googleToken ||
      user?.googleRefreshToken,
  );
  const hasMetaToken = Boolean(accountTokens?.facebookToken || user?.facebookToken);
  const hasShopifyToken = Boolean(accountTokens?.shopifyToken || user?.shopifyToken);

  const google = useMemo(() => {
    const connected =
      rawGoogle?.connected ||
      accountTokens?.hasGoogleConnection ||
      hasGoogleToken;
    const analyticsConnected =
      rawGoogle?.analyticsConnected || accountTokens?.hasGoogleAnalytics;
    const searchConsoleConnected =
      rawGoogle?.searchConsoleConnected || accountTokens?.hasSearchConsole;
    const merchantCenterConnected =
      rawGoogle?.merchantCenterConnected || accountTokens?.hasMerchantCenter;

    return {
      ...rawGoogle,
      connected,
      analyticsConnected,
      searchConsoleConnected,
      merchantCenterConnected,
      status: rawGoogle?.status || (connected ? "connected" : "not_connected"),
      detail:
        rawGoogle?.detail ||
        (connected ? "Google account connected" : "Needs Google connection"),
    };
  }, [rawGoogle, accountTokens, hasGoogleToken]);

  const meta = useMemo(() => {
    const connected =
      rawMeta?.connected ||
      accountTokens?.hasMetaConnection ||
      hasMetaToken;
    const adsConnected =
      rawMeta?.adsConnected || accountTokens?.hasMetaAdsManager;
    const instagramConnected =
      rawMeta?.instagramConnected || accountTokens?.hasInstagramBusiness;
    const pixelConnected =
      rawMeta?.pixelConnected || accountTokens?.hasMetaPixel;
    const businessConnected =
      rawMeta?.businessConnected || accountTokens?.hasMetaBusinessPage;

    return {
      ...rawMeta,
      connected,
      adsConnected,
      instagramConnected,
      pixelConnected,
      businessConnected,
      status: rawMeta?.status || (connected ? "connected" : "not_connected"),
      detail:
        rawMeta?.detail ||
        (connected ? "Meta account connected" : "Needs Meta connection"),
    };
  }, [rawMeta, accountTokens, hasMetaToken]);

  const shopify = useMemo(() => {
    const connected =
      rawShopify?.connected ||
      accountTokens?.hasShopifyConnection ||
      hasShopifyToken ||
      Boolean(shopData?._id || shopData?.id || shopData?.myshopifyDomain);

    return {
      ...rawShopify,
      connected,
      status: rawShopify?.status || (connected ? "connected" : "not_connected"),
      detail:
        rawShopify?.detail ||
        (connected ? "Shopify store connected" : "No Shopify store connected"),
    };
  }, [rawShopify, accountTokens, hasShopifyToken, shopData]);

  const handleIntegrationUpdated = async () => {
    await Promise.allSettled([
      refetch?.(),
      refetchTokens?.(),
      refreshShops?.(),
      refreshUser?.(),
    ]);
  };

  const handleDisconnect = (mutationFn) => async () => {
    try {
      await mutationFn();
      await handleIntegrationUpdated();
    } catch (err) {
      console.error("[integrations] disconnect failed", err);
    }
  };

  const primaryEmail = shopData?.email || user?.email || null;

  const googleSecondaryAction = google?.connected
    ? [
        google.analyticsConnected ? "GA4" : null,
        google.searchConsoleConnected ? "GSC" : null,
        google.merchantCenterConnected ? "GMC" : null,
      ]
        .filter(Boolean)
        .join(" • ") || null
    : null;

  const metaSecondaryAction = meta?.connected
    ? [
        meta.adsConnected ? "Ads" : null,
        meta.instagramConnected ? "Instagram" : null,
        meta.pixelConnected ? "Pixel" : null,
      ]
        .filter(Boolean)
        .join(" • ") || null
    : null;

  const sections = useMemo(
    () => [
      {
        title: "Commerce Platforms",
        items: [
          {
            key: "shopify",
            title: "Shopify",
            icon: (
              <LogoImage
                src="/assets/shopify_white_logo_clearBG.png"
                alt="Shopify"
              />
            ),
            brandClassName: styles.brandShopify,
            statusLabel: labelFor(shopify?.status, checking),
            statusTone: toneFor(shopify?.status),
            detail: checking ? null : (shopify?.detail || "No Shopify store connected"),
            primaryAction: checking ? null : shopify?.connected ? null : "Connect",
            onClick: shopify?.connected ? undefined : openShopifyConnect,
          },
          {
            key: "woocommerce",
            title: "WooCommerce",
            icon: <WooCommerceIcon />,
            brandClassName: styles.brandWoo,
            statusLabel: "Coming soon",
            statusTone: "muted",
            detail: "Commerce integration coming soon",
            badge: "Coming soon",
            disabled: true,
          },
          {
            key: "request",
            title: "Request integration",
            icon: <Plus size={18} />,
            brandClassName: styles.brandMuted,
            statusLabel: "Contact us",
            statusTone: "warning",
            detail: "Tell us what to support next",
            primaryAction: "Open request form",
            onClick: () => supportCenter?.openRequestIntegration?.(),
          },
        ],
      },
      {
        title: "Advertising Platforms",
        items: [
          {
            key: "meta",
            title: "Meta Ads",
            icon: <MetaIcon />,
            brandClassName: styles.brandMeta,
            statusLabel: checking
              ? "Checking connection..."
              : meta?.connected
                ? "Connected"
                : "Not connected",
            statusTone: checking ? "muted" : meta?.connected ? "connected" : "muted",
            detail: checking
              ? null
              : meta?.adsConnected
                ? "Ads Manager connected"
                : meta?.connected
                  ? "Meta connected — link Ads Manager"
                  : "Needs Meta Ads Manager connection",
            secondaryAction: metaSecondaryAction,
            secondaryTone: "accent",
            primaryAction: checking ? null : (meta?.connected ? (disconnectingMeta ? "Disconnecting..." : "Disconnect") : "Connect"),
            primaryActionOnClick: meta?.connected ? handleDisconnect(runDisconnectMeta) : null,
            primaryActionTone: meta?.connected ? "danger" : "default",
            onClick: meta?.connected ? undefined : () => setShowMetaModal(true),
          },
          {
            key: "google-ads",
            title: "Google Ads",
            icon: <GoogleIcon />,
            brandClassName: styles.brandGoogle,
            statusLabel: labelFor(google?.status, checking),
            statusTone: toneFor(google?.status),
            detail: checking ? null : (google?.detail || "Needs Google connection"),
            secondaryAction: googleSecondaryAction,
            secondaryTone: "accent",
            primaryAction: checking ? null : (google?.connected ? (disconnectingGoogle ? "Disconnecting..." : "Disconnect") : "Connect"),
            primaryActionOnClick: google?.connected ? handleDisconnect(runDisconnectGoogle) : null,
            primaryActionTone: google?.connected ? "danger" : "default",
            onClick: google?.connected ? undefined : () => setShowGoogleModal(true),
          },
          {
            key: "tiktok-ads",
            title: "TikTok Ads",
            icon: <TikTokIcon />,
            brandClassName: styles.brandTikTok,
            statusLabel: labelFor(
              tiktok?.adsConnected ? tiktok.status : "not_connected",
              checking,
            ),
            statusTone: toneFor(tiktok?.adsConnected ? tiktok.status : "not_connected"),
            detail: checking
              ? null
              : tiktok?.adsConnected
                ? tiktok.detail
                : "Needs TikTok Ads connection",
            primaryAction: checking ? null : (tiktok?.connected ? (disconnectingTikTok ? "Disconnecting..." : "Disconnect") : "Connect"),
            primaryActionOnClick: tiktok?.connected ? handleDisconnect(runDisconnectTikTok) : null,
            primaryActionTone: tiktok?.connected ? "danger" : "default",
            onClick: tiktok?.connected ? undefined : () => setShowTikTokModal(true),
          },
        ],
      },
      {
        title: "Content posting",
        items: [
          {
            key: "instagram",
            title: "Instagram",
            icon: <InstagramIcon />,
            brandClassName: styles.brandInstagram,
            statusLabel: checking ? "Checking..." : meta?.instagramConnected ? "Connected" : "Not connected",
            statusTone: meta?.instagramConnected ? "connected" : "muted",
            detail: checking ? null : meta?.instagramConnected ? "Connected via Meta" : "Not connected",
            primaryAction: checking ? null : (meta?.instagramConnected ? (disconnectingMeta ? "Disconnecting..." : "Disconnect") : "Connect via Meta"),
            primaryActionOnClick: meta?.instagramConnected ? handleDisconnect(runDisconnectMeta) : null,
            primaryActionTone: meta?.instagramConnected ? "danger" : "default",
            onClick: meta?.instagramConnected ? undefined : () => setShowMetaModal(true),
          },
          {
            key: "x",
            title: "X (Twitter)",
            icon: <XIcon />,
            brandClassName: styles.brandX,
            statusLabel: "Coming soon",
            statusTone: "muted",
            detail: "Not yet available in Zeqi",
            badge: "Coming soon",
          },
          {
            key: "tiktok",
            title: "TikTok",
            icon: <TikTokIcon />,
            brandClassName: styles.brandTikTok,
            statusLabel: checking
              ? "Checking..."
              : tiktok?.creatorConnected
                ? "Connected"
                : "Not connected",
            statusTone: tiktok?.creatorConnected ? "connected" : "muted",
            detail: checking
              ? null
              : tiktok?.creatorConnected
                ? shopData?.name || "Connected account"
                : "No TikTok content connection",
          },
        ],
      },
      {
        title: "Analytics & search",
        items: [
          {
            key: "ga",
            title: "Google Analytics",
            icon: <AnalyticsIcon />,
            brandClassName: styles.brandAnalytics,
            statusLabel: checking ? "Checking..." : google?.analyticsConnected ? "Connected" : "Not connected",
            statusTone: google?.analyticsConnected ? "connected" : "muted",
            detail: checking
              ? null
              : google?.analyticsConnected
                ? primaryEmail || "GA4 connected"
                : "No GA4 property detected",
          },
          {
            key: "gsc",
            title: "Google Search Console",
            icon: <SearchConsoleIcon />,
            brandClassName: styles.brandGoogle,
            statusLabel: checking ? "Checking..." : google?.searchConsoleConnected ? "Connected" : "Not connected",
            statusTone: google?.searchConsoleConnected ? "connected" : "muted",
            detail: checking
              ? null
              : google?.searchConsoleConnected
                ? primaryEmail ? `Connected via: ${primaryEmail}` : "Connected"
                : "Needs connection",
          },
          {
            key: "gmc",
            title: "Google Merchant Center",
            icon: <MerchantCenterIcon />,
            brandClassName: styles.brandGoogle,
            statusLabel: checking ? "Checking..." : google?.merchantCenterConnected ? "Connected" : "Not connected",
            statusTone: google?.merchantCenterConnected ? "connected" : "muted",
            detail: checking
              ? null
              : google?.merchantCenterConnected
                ? primaryEmail || "Merchant Center connected"
                : "No Merchant Center detected",
          },
        ],
      },
      {
        title: "Email",
        items: [
          {
            key: "klaviyo",
            title: "Klaviyo",
            icon: <KlaviyoIcon />,
            brandClassName: styles.brandKlaviyo,
            statusLabel: "Coming soon",
            statusTone: "muted",
            detail: "Email channel integration",
            badge: "Coming soon",
            disabled: true,
          },
        ],
      },
    ],
    [
      checking,
      shopify,
      google,
      meta,
      tiktok,
      googleSecondaryAction,
      metaSecondaryAction,
      primaryEmail,
      shopData?.name,
      supportCenter,
      disconnectingGoogle,
      disconnectingMeta,
      disconnectingTikTok,
      openShopifyConnect,
      t,
    ],
  );

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.header}>
          <div>
            <p className={styles.subtitle}>
              Connect your marketing and analytics accounts. Even with one-click
              connection, some tools may still require separate authentication
              when they live under different accounts.
            </p>
          </div>
        </div>

        <div className={styles.sections}>
          {sections.map((section) => (
            <IntegrationSection key={section.title} title={section.title}>
              {section.items.map(({ key, ...item }) => (
                <IntegrationCard key={key} {...item} />
              ))}
            </IntegrationSection>
          ))}
        </div>
      </div>

      <GoogleConnectModal
        visible={showGoogleModal}
        onClose={() => setShowGoogleModal(false)}
        onConnected={handleIntegrationUpdated}
      />
      <MetaConnectModal
        visible={showMetaModal}
        onClose={() => setShowMetaModal(false)}
        onConnected={handleIntegrationUpdated}
      />
      <TikTokConnectModal
        visible={showTikTokModal}
        onClose={() => setShowTikTokModal(false)}
        onConnected={handleIntegrationUpdated}
      />
    </>
  );
};

export default IntegrationsPanel;
